import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Trash2, Plus, Minus, ShoppingCart, ArrowLeft, CheckCircle, Loader2, Tag, CheckCheck, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const FREE_CASE_ID = 'free-case-9';
const FREE_HOODIE_ID = 'free-hoodie';
const HOODIE_STYLE_IDS = ['hoodie-logo', 'hoodie-patch'];
const HOODIE_STYLES = [
  { key: 'logo-front-only', productId: 'hoodie-logo', label: 'Front Logo Only' },
  { key: 'logo-front-back', productId: 'hoodie-logo', label: 'Front + Back Logo' },
  { key: 'patch', productId: 'hoodie-patch', label: 'Leather Patch' },
];
const HOODIE_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL'];
const NON_PEPTIDE_CATEGORIES = ['Accessories', 'Skincare', 'Apparel'];

const fmtPromoDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

export const CartPage = () => {
  const { cart, addToCart, removeFromCart, updateQty, clearCart, cartTotal } = useCart();
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [address, setAddress] = useState({ name: '', street: '', city: '', province: '', postal: '', phone: '' });
  const [customerEmail, setCustomerEmail] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.email && !customerEmail) setCustomerEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const [subscribeNews, setSubscribeNews] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/member/addresses`, { withCredentials: true }).then(r => setSavedAddresses(r.data || [])).catch(() => {});
    }
  }, [user]);
  const [addressError, setAddressError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [activePromo, setActivePromo] = useState(null); // { code, discount }
  const [promoConfig, setPromoConfig] = useState(null);
  const [bacWaterProduct, setBacWaterProduct] = useState(null);
  const [showBacNotice, setShowBacNotice] = useState(false);
  const [shipMethod, setShipMethod] = useState('standard');
  const [hoodiePromo, setHoodiePromo] = useState(null);
  const [hoodieProducts, setHoodieProducts] = useState([]);
  const [freeHoodieStyle, setFreeHoodieStyle] = useState(null);
  const [freeHoodieSize, setFreeHoodieSize] = useState(null);

  const SHIPPING_OPTIONS = [
    { id: 'standard', label: 'Standard Express Post', price: 20, desc: 'Express Post shipping to all provinces.' },
    { id: 'express_on', label: 'Ontario Express Post', price: 30, desc: 'Faster Express Post delivery for Ontario addresses.' },
  ];
  const isPickup = shipMethod === 'pickup';

  useEffect(() => {
    axios.get(`${API}/promo-config`)
      .then(res => setPromoConfig(res.data))
      .catch(() => setPromoConfig(null));
    axios.get(`${API}/hoodie-promo`)
      .then(res => setHoodiePromo(res.data))
      .catch(() => setHoodiePromo(null));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Sync cart prices and remove out-of-stock items
    const syncCart = async () => {
      try {
        const res = await axios.get(`${API}/peptides`);
        const productMap = {};
        res.data.forEach(p => { productMap[p.id] = p; });
        setHoodieProducts(HOODIE_STYLE_IDS.map(id => res.data.find(p => p.id === id)).filter(Boolean));
        setBacWaterProduct(
          res.data.find(p => p.id === 'bac-water-10ml' && !p.out_of_stock) ||
          res.data.find(p => p.name.toUpperCase().includes('BACTERIOSTATIC') && !p.out_of_stock) ||
          null
        );
        cart.forEach(item => {
          const key = item.cartKey || item.id;
          const serverProduct = productMap[item.id];
          if (serverProduct) {
            if (serverProduct.out_of_stock) {
              removeFromCart(key);
            } else if (serverProduct.price !== item.price) {
              updateQty(key, item.qty, serverProduct.price);
            }
          }
        });
      } catch (err) {
        console.error('Failed to sync cart:', err);
      }
    };
    if (cart.length > 0) syncCart();
    else axios.get(`${API}/peptides`).then(res => setHoodieProducts(HOODIE_STYLE_IDS.map(id => res.data.find(p => p.id === id)).filter(Boolean))).catch(() => {});
  }, []);

  // Track potentially abandoned carts once we have an email
  useEffect(() => {
    if (orderPlaced) return;
    const email = customerEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) || cart.length === 0) return;
    const t = setTimeout(() => {
      axios.post(`${API}/cart/track`, {
        email,
        items: cart.filter(i => i.id !== FREE_CASE_ID && i.id !== FREE_HOODIE_ID).map(i => ({
          id: i.id,
          name: i.variantLabel ? `${i.name} (${i.variantLabel})` : i.name,
          dosage: i.dosage,
          price: i.price,
          qty: i.qty
        })),
        total: cartTotal
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerEmail, cart, orderPlaced]);

  const SHIPPING_COST = { pickup: 0, standard: 20, express_on: 30 }[shipMethod];
  const CWF08_UNIT_DISCOUNTS = { klow: 25, glow: 20 };

  // Calculate discount based on active promo
  const discountPercent = activePromo ? activePromo.discount : 0;
  const cwf08Amount = cart.reduce((sum, item) => sum + (CWF08_UNIT_DISCOUNTS[item.id] || 0) * item.qty, 0);
  const halloweenAmount = promoApplied && activePromo?.halloween
    ? cart.reduce((sum, item) => {
        const sale = activePromo.salePrices?.[item.id];
        if (!sale) return sum;
        const diff = (parseFloat(item.price.replace('$', '')) || 0) - (parseFloat(String(sale).replace('$', '')) || 0);
        return sum + Math.max(0, diff) * item.qty;
      }, 0)
    : 0;
  const discountAmount = promoApplied && activePromo
    ? (activePromo.halloween
        ? Math.min(halloweenAmount, cartTotal)
        : activePromo.fixed
        ? Math.min(cwf08Amount, cartTotal)
        : cart.reduce((sum, item) => {
            if (NON_PEPTIDE_CATEGORIES.includes(item.category)) return sum;
            if (item.name.toUpperCase().includes('BAC WATER') || item.name.toUpperCase().includes('BACTERIOSTATIC WATER') || item.name.toUpperCase().includes('ACETIC ACID') || item.name.toUpperCase().includes('SYRINGE') || item.name.toUpperCase().includes('PEPTIDE CASE') || item.name.toUpperCase().includes('COMBO')) return sum;
            const price = parseFloat(item.price.replace('$', '')) || 0;
            return sum + (price * item.qty * discountPercent / 100);
          }, 0))
    : 0;

  const discountedSubtotal = cartTotal - discountAmount;
  const orderTotal = discountedSubtotal + SHIPPING_COST;

  const peptideSubtotal = cart.reduce((sum, item) => {
    if (NON_PEPTIDE_CATEGORIES.includes(item.category)) return sum;
    return sum + (parseFloat(item.price.replace('$', '')) || 0) * item.qty;
  }, 0);

  const hasPeptidesInCart = cart.some(i => i.id !== FREE_CASE_ID && !NON_PEPTIDE_CATEGORIES.includes(i.category) && !(i.name || '').toUpperCase().includes('CAPSULE'));
  const hasBacWaterInCart = cart.some(i => (i.name || '').toUpperCase().includes('BACTERIOSTATIC') || (i.name || '').toUpperCase().includes('BAC WATER'));
  const showBacWaterUpsell = hasPeptidesInCart && !hasBacWaterInCart && !!bacWaterProduct;

  const promoActive = !!(promoConfig?.active && promoConfig?.free_item);
  const promoMinSpend = promoConfig?.min_spend ?? 350;
  const promoCodeName = promoConfig?.code || 'CASE10';
  const freeCaseItem = promoConfig?.free_item ? {
    id: FREE_CASE_ID,
    name: `FREE ${promoConfig.free_item.name}`,
    dosage: promoConfig.free_item.dosage,
    category: 'Accessories',
    price: '$0',
    image: promoConfig.free_item.image
  } : null;

  // Remove the free case if the promo ends or the peptide total drops below the minimum
  useEffect(() => {
    if (!promoConfig) return;
    const hasFreeCase = cart.some(i => i.id === FREE_CASE_ID);
    if (!hasFreeCase) return;
    if (!promoActive || peptideSubtotal < promoMinSpend) {
      removeFromCart(FREE_CASE_ID);
      if (activePromo?.freeCase) {
        setPromoApplied(false);
        setActivePromo(null);
        setPromoCode('');
        setPromoError(`${promoCodeName} removed — you need at least $${Math.round(promoMinSpend)} in peptides for the free case.`);
      }
    }
  }, [cart, peptideSubtotal, activePromo, promoConfig, promoActive, promoMinSpend, promoCodeName, removeFromCart]);

  // Drop CWF08 if the cart no longer contains KLOW or GLOW
  useEffect(() => {
    if (promoApplied && activePromo?.fixed && cwf08Amount === 0) {
      setPromoApplied(false);
      setActivePromo(null);
      setPromoCode('');
      setPromoError('CWF08 removed — it only applies to KLOW or GLOW.');
    }
  }, [cwf08Amount, promoApplied, activePromo]);

  // Free hoodie (HOODIE10) — remove if promo dropped or peptide total falls below $400
  const freeHoodieInCart = cart.find(i => i.id === FREE_HOODIE_ID);
  const hoodieMinSpend = hoodiePromo?.min_spend ?? 400;
  useEffect(() => {
    if (freeHoodieInCart && !activePromo?.freeHoodie) {
      removeFromCart(freeHoodieInCart.cartKey || freeHoodieInCart.id);
      return;
    }
    if (activePromo?.freeHoodie && peptideSubtotal < hoodieMinSpend) {
      if (freeHoodieInCart) removeFromCart(freeHoodieInCart.cartKey || freeHoodieInCart.id);
      setPromoApplied(false);
      setActivePromo(null);
      setPromoCode('');
      setPromoError(`HOODIE10 removed — you need at least $${Math.round(hoodieMinSpend)} in peptides for the free hoodie.`);
    }
  }, [cart, peptideSubtotal, activePromo, freeHoodieInCart, hoodieMinSpend, removeFromCart]);

  const handleAddFreeHoodie = () => {
    if (!freeHoodieStyle || !freeHoodieSize) return;
    const style = HOODIE_STYLES.find(s => s.key === freeHoodieStyle);
    const product = style && hoodieProducts.find(p => p.id === style.productId);
    if (!product) return;
    if (freeHoodieInCart) removeFromCart(freeHoodieInCart.cartKey || freeHoodieInCart.id);
    addToCart({
      id: FREE_HOODIE_ID,
      name: `FREE ${product.name}`,
      dosage: style.label,
      category: 'Apparel',
      price: '$0',
      image: product.image
    }, { label: `${style.label} • Size ${freeHoodieSize}` });
  };

  const handleApplyPromo = async () => {
    setPromoError('');

    // Validate via backend
    try {
      const res = await axios.post(`${API}/validate-promo`, {
        code: promoCode.trim(),
        email: customerEmail.trim() || null,
        peptide_subtotal: peptideSubtotal,
        items: cart.map(i => ({ id: i.id, qty: i.qty }))
      });
      if (res.data.valid) {
        setPromoApplied(true);
        setActivePromo({ code: res.data.code, discount: res.data.discount, freeCase: !!res.data.free_case, freeHoodie: !!res.data.free_hoodie, halloween: !!res.data.halloween, salePrices: res.data.sale_prices || null, stackedBonus: !!res.data.stacked_bonus, baseDiscount: res.data.base_discount, fixed: res.data.fixed_discount && !res.data.halloween ? res.data.fixed_discount : 0 });
        setPromoError('');
        if (res.data.free_case && freeCaseItem && !cart.some(i => i.id === FREE_CASE_ID)) {
          addToCart(freeCaseItem);
        }
      } else {
        setPromoError(res.data.error || 'Invalid promo code.');
        setPromoApplied(false);
        setActivePromo(null);
      }
    } catch {
      setPromoError('Unable to validate promo code. Please try again.');
    }
  };

  const handleRemovePromo = () => {
    if (activePromo?.freeCase) {
      removeFromCart(FREE_CASE_ID);
    }
    if (activePromo?.freeHoodie && freeHoodieInCart) {
      removeFromCart(freeHoodieInCart.cartKey || freeHoodieInCart.id);
    }
    setPromoApplied(false);
    setActivePromo(null);
    setPromoCode('');
    setPromoError('');
  };

  const handleCheckout = async () => {
    if (!address.name.trim() || (!isPickup && (!address.street.trim() || !address.city.trim() || !address.province.trim() || !address.postal.trim()))) {
      setAddressError(isPickup ? 'Please enter your name.' : 'Please fill in all address fields before placing your order.');
      return;
    }
    if (!address.phone.trim()) {
      setAddressError('Please enter your phone number so we can reach you if there is an issue with your order.');
      return;
    }
    if (!customerEmail.trim()) {
      setAddressError('Please enter your email address.');
      return;
    }
    setAddressError('');
    if (activePromo?.freeHoodie && !cart.some(i => i.id === FREE_HOODIE_ID)) {
      setAddressError('Choose your free hoodie style and size above before placing your order.');
      return;
    }
    if (cart.some(i => i.id === 'bac-water-10ml')) {
      setShowBacNotice(true);
      return;
    }
    await submitOrder();
  };

  const submitOrder = async () => {
    setSubmitting(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          id: item.id,
          name: item.variantLabel ? `${item.name} (${item.variantLabel})` : item.name,
          dosage: item.dosage,
          price: item.price,
          qty: item.qty
        })),
        address,
        subtotal: cartTotal,
        discount: discountAmount,
        promo_code: promoApplied && activePromo ? activePromo.code : null,
        shipping: SHIPPING_COST,
        shipping_method: shipMethod,
        total: orderTotal,
        customer_email: customerEmail || null,
        subscribe: subscribeNews
      };

      const res = await axios.post(`${API}/orders`, orderData);
      setOrderNumber(res.data.order_number);
      setOrderPlaced(true);
      setShowReminder(true);
    } catch (err) {
      console.error('Order submission failed:', err);
      setAddressError('Something went wrong submitting your order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-[#050505] py-12" data-testid="order-confirmation">
        {/* Reminder Popup */}
        {showReminder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="reminder-popup">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowReminder(false)} />
            <div className="relative bg-[#0A0A0A] border border-[#D4AF37] rounded-lg p-8 max-w-md w-full shadow-2xl animate-fade-in-up">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C41E3A] via-[#D4AF37] to-[#C41E3A]" />
              <div className="text-center mb-5">
                <div className="bg-[#D4AF37]/10 rounded-full p-4 inline-block mb-4">
                  <CheckCircle size={40} className="text-[#D4AF37]" />
                </div>
                <h2 className="font-['Oswald'] text-2xl uppercase tracking-wide text-white mb-2">{"Don't Forget!"}</h2>
              </div>
              <div className="space-y-4 mb-6">
                <p className="text-gray-200 text-sm text-center">
                  Please send your <span className="text-[#D4AF37] font-bold">Interac e-Transfer</span> to complete your order so we can get it shipped out to you right away!
                </p>
                <div className="bg-[#121212] border border-[#27272A] rounded-sm p-3 text-center">
                  <p className="text-[#D4AF37] font-bold">canadianwellness2026@outlook.com</p>
                </div>
                <p className="text-gray-300 text-sm text-center">
                  {isPickup
                    ? <>We will <span className="text-white font-semibold">contact you to arrange your pickup</span> in Airdrie once payment is confirmed.</>
                    : <>We will send your <span className="text-white font-semibold">tracking number via email</span> once payment is confirmed.</>}
                </p>
                <p className="text-gray-400 text-xs text-center bg-[#121212] rounded-sm p-3 border border-[#27272A]">
                  Please <span className="text-[#D4AF37] font-semibold">check your junk/spam folder</span> if you {"don't"} see our email in your inbox.
                </p>
              </div>
              <button
                onClick={() => setShowReminder(false)}
                className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-all duration-300"
                data-testid="reminder-popup-close"
              >
                Got It!
              </button>
            </div>
          </div>
        )}
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-8">
            <CheckCircle size={64} className="text-[#D4AF37] mx-auto mb-4" />
            <h1 className="font-['Oswald'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-2">
              Order Placed
            </h1>
            <p className="text-gray-400">Your order has been submitted successfully.</p>
          </div>

          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 mb-6">
            <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-4">Your Order Number</h2>
            <div className="bg-[#121212] border border-[#C41E3A] rounded-sm p-4 text-center mb-6">
              <p className="text-[#C41E3A] text-3xl font-bold font-mono tracking-widest" data-testid="order-number">
                {orderNumber}
              </p>
            </div>

            <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-4">Payment Instructions</h2>
            <div className="space-y-3 text-gray-300 text-sm">
              <p>1. Your order has been <span className="text-[#D4AF37] font-semibold">automatically sent to us</span> with all your details. No email needed from you!</p>
              <p>2. Send an <span className="text-[#D4AF37] font-semibold">Interac e-Transfer</span> for the total amount to:</p>
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-3 text-center">
                <p className="text-[#D4AF37] text-lg font-bold">canadianwellness2026@outlook.com</p>
              </div>
              <p>3. In the <span className="text-[#D4AF37] font-semibold">message box</span> of your e-Transfer, include your <span className="text-[#D4AF37] font-semibold">order number</span>:</p>
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-3 text-center">
                <p className="text-white font-mono font-bold">{orderNumber}</p>
              </div>
              <p>4. {isPickup
                ? <>Once your e-Transfer has been received and confirmed, we will <span className="text-[#D4AF37] font-semibold">contact you to arrange your pickup in Airdrie</span>.</>
                : <>Once your e-Transfer has been received and confirmed, we will process your order and send you a tracking number via email.</>}</p>
            </div>
          </div>

          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 mb-6">
            <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-4">Order Summary</h2>
            {cart.map(item => (
              <div key={item.cartKey || item.id} className="flex justify-between items-center py-2 border-b border-[#27272A] last:border-0">
                <div>
                  <p className="text-white text-sm">
                    {item.name}
                    {item.variantLabel && <span className="text-[#C41E3A]"> ({item.variantLabel})</span>}
                    {' '}<span className="text-gray-500">x{item.qty}</span>
                  </p>
                  <p className="text-gray-500 text-xs">{item.dosage}</p>
                </div>
                <p className="text-[#D4AF37] font-semibold">
                  {item.id === FREE_CASE_ID ? <span className="text-green-400">FREE</span> : `$${(parseFloat(item.price.replace('$', '')) * item.qty).toFixed(2)}`}
                </p>
              </div>
            ))}
            <div className="flex justify-between items-center pt-2 border-t border-[#27272A]">
              <p className="text-gray-400 text-sm">Subtotal</p>
              <p className="text-white font-semibold">${cartTotal.toFixed(2)}</p>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between items-center pt-1">
                <p className="text-green-400 text-sm">Discount ({activePromo?.code} — {activePromo?.discount}% off peptides)</p>
                <p className="text-green-400 font-semibold">-${discountAmount.toFixed(2)}</p>
              </div>
            )}
            <div className="flex justify-between items-center pt-1">
              <p className="text-gray-400 text-sm">Shipping</p>
              <p className="text-white font-semibold">$20.00</p>
            </div>
            <div className="flex justify-between items-center pt-2 mt-2 border-t border-[#C41E3A]">
              <p className="text-white font-bold text-lg uppercase">Total</p>
              <p className="text-[#C41E3A] text-2xl font-bold" data-testid="order-total">${orderTotal.toFixed(2)}</p>
            </div>
            <p className="text-[#D4AF37] text-xs mt-1">Shipping added to total price</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              onClick={clearCart}
              className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white text-center font-semibold uppercase tracking-wider py-3 px-6 rounded-sm transition-all duration-300"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center px-4">
          <ShoppingCart size={64} className="text-gray-600 mx-auto mb-4" />
          <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-tight text-white mb-2">Your Cart is Empty</h1>
          <p className="text-gray-400 mb-6">Browse our catalog and add products to your cart.</p>
          <Link
            to="/#products"
            className="inline-block bg-[#C41E3A] hover:bg-[#A01830] text-white font-semibold uppercase tracking-wider py-3 px-8 rounded-sm transition-all duration-300"
            data-testid="browse-products-btn"
          >
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="cart-page">
      <meta name="robots" content="noindex" />
      <title>Cart | Canadian Wellness &amp; Fitness</title>
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/#products" className="inline-flex items-center text-gray-400 hover:text-[#C41E3A] transition-colors mb-2">
              <ArrowLeft size={18} className="mr-2" />
              <span className="text-sm uppercase tracking-wider font-semibold">Continue Shopping</span>
            </Link>
            <h1 className="font-['Oswald'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
              Your Cart
            </h1>
          </div>
          <button
            onClick={clearCart}
            className="text-gray-500 hover:text-[#C41E3A] text-sm uppercase tracking-wider transition-colors"
            data-testid="clear-cart-btn"
          >
            Clear All
          </button>
        </div>

        {/* Cart Items */}
        <div className="space-y-4 mb-8">
          {cart.map(item => {
            const key = item.cartKey || item.id;
            const isFreeCase = item.id === FREE_CASE_ID;
            const isFreeHoodie = item.id === FREE_HOODIE_ID;
            const isFreeItem = isFreeCase || isFreeHoodie;
            const halloweenSale = promoApplied && activePromo?.halloween ? activePromo.salePrices?.[item.id] : null;
            const effUnitPrice = halloweenSale ? parseFloat(String(halloweenSale).replace('$', '')) : parseFloat(item.price.replace('$', ''));
            return (
            <div key={key} className={`bg-[#0A0A0A] border ${isFreeItem ? 'border-green-600/60' : 'border-[#27272A]'} rounded-sm p-4 flex items-center gap-4`} data-testid={`cart-item-${key}`}>
              <div className="w-20 h-20 flex-shrink-0 bg-white rounded-sm overflow-hidden">
                <img src={item.variantImage || item.image} alt={item.name} className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-['Oswald'] text-white text-lg uppercase tracking-wide truncate">
                  {item.name}
                  {item.variantLabel && <span className="text-[#C41E3A] text-sm ml-2">({item.variantLabel})</span>}
                </h3>
                <p className="text-[#D4AF37] text-sm">{item.dosage}</p>
                {isFreeItem ? (
                  <p className="text-green-400 text-sm font-semibold">FREE with code {isFreeHoodie ? 'HOODIE10' : promoCodeName}</p>
                ) : halloweenSale ? (
                  <p className="text-sm" data-testid={`halloween-price-${item.id}`}>
                    <span className="line-through text-gray-600">{item.price}</span>{' '}
                    <span className="text-[#FF8C00] font-bold">{halloweenSale} each</span>
                  </p>
                ) : (
                  <p className="text-gray-400 text-sm">{item.price} each</p>
                )}
              </div>
              {!isFreeItem && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateQty(key, item.qty - 1)}
                  className="w-8 h-8 flex items-center justify-center border border-[#27272A] rounded-sm text-gray-400 hover:text-white hover:border-[#C41E3A] transition-colors"
                  data-testid={`qty-minus-${key}`}
                >
                  <Minus size={14} />
                </button>
                <span className="w-8 text-center text-white font-semibold" data-testid={`qty-${key}`}>{item.qty}</span>
                {item.id === 'bac-water' ? (
                  <span className="text-gray-500 text-[10px] uppercase tracking-wider w-8 text-center leading-tight" data-testid="bac-water-limit-note">Max 1</span>
                ) : (
                <button
                  onClick={() => updateQty(key, item.qty + 1)}
                  className="w-8 h-8 flex items-center justify-center border border-[#27272A] rounded-sm text-gray-400 hover:text-white hover:border-[#C41E3A] transition-colors"
                  data-testid={`qty-plus-${key}`}
                >
                  <Plus size={14} />
                </button>
                )}
              </div>
              )}
              <p className={`font-bold text-lg w-20 text-right ${isFreeItem ? 'text-green-400' : halloweenSale ? 'text-[#FF8C00]' : 'text-[#C41E3A]'}`}>
                {isFreeItem ? 'FREE' : `$${(effUnitPrice * item.qty).toFixed(2)}`}
              </p>
              <button
                onClick={() => removeFromCart(key)}
                className="text-gray-500 hover:text-[#C41E3A] transition-colors"
                data-testid={`remove-${key}`}
              >
                <Trash2 size={18} />
              </button>
            </div>
            );
          })}
        </div>

        {/* Free Case Promo Progress (only while promo is active) */}
        {promoActive && (
          <div className="bg-[#0A0A0A] border border-[#C41E3A]/60 rounded-sm p-4 mb-4" data-testid="free-case-progress">
            {peptideSubtotal >= promoMinSpend ? (
              activePromo?.freeCase ? (
                <p className="text-green-400 text-sm font-semibold flex items-center gap-2">
                  <Gift size={16} />
                  Your FREE {promoConfig.free_item.name.toLowerCase()} is in the cart! 🎉
                </p>
              ) : (
                <p className="text-[#D4AF37] text-sm font-semibold flex items-center gap-2">
                  <Gift size={16} className="text-[#C41E3A]" />
                  You qualify for a FREE {promoConfig.free_item.name.toLowerCase()} — enter code <span className="text-white tracking-widest">{promoCodeName}</span> below!
                </p>
              )
            ) : (
              <>
                <p className="text-white text-sm font-semibold flex items-center gap-2 mb-2">
                  <Gift size={16} className="text-[#C41E3A]" />
                  You're <span className="text-[#D4AF37]">${(promoMinSpend - peptideSubtotal).toFixed(2)}</span> in peptides away from a FREE {promoConfig.free_item.name.toLowerCase()}!
                </p>
                <div className="w-full h-2.5 bg-[#27272A] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#C41E3A] to-[#D4AF37] rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, (peptideSubtotal / promoMinSpend) * 100)}%` }}
                  />
                </div>
                <p className="text-gray-500 text-[11px] mt-1.5">
                  Spend ${Math.round(promoMinSpend)}+ on peptides ({fmtPromoDate(promoConfig.start_date)} – {fmtPromoDate(promoConfig.end_date)}) and get our {promoConfig.free_item.name.toLowerCase()} FREE with code {promoCodeName}. Shipping, cases, syringes, bac water &amp; skincare not included.
                </p>
              </>
            )}
          </div>
        )}

        {/* Free Hoodie Picker (HOODIE10) */}
        {promoApplied && activePromo?.freeHoodie && (
          <div className="bg-[#0A0A0A] border border-[#D4AF37] rounded-sm p-5 mb-4" data-testid="free-hoodie-picker">
            <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-1 flex items-center gap-2">
              <Gift size={16} className="text-[#C41E3A]" />
              Choose Your Free Hoodie
            </p>
            <p className="text-gray-400 text-xs mb-4">
              HOODIE10 unlocked — pick a style and size and we&apos;ll add it to your order for free.
              {hoodiePromo?.remaining ? ` Only ${hoodiePromo.remaining} of 5 free hoodies left!` : ''}
            </p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {HOODIE_STYLES.map(s => {
                const product = hoodieProducts.find(p => p.id === s.productId);
                if (!product) return null;
                return (
                <button
                  key={s.key}
                  onClick={() => setFreeHoodieStyle(s.key)}
                  className={`border rounded-sm overflow-hidden text-left transition-all duration-200 ${
                    freeHoodieStyle === s.key ? 'border-[#C41E3A] bg-[#C41E3A]/10' : 'border-[#27272A] hover:border-gray-500'
                  }`}
                  data-testid={`free-hoodie-style-${s.key}`}
                >
                  <img src={product.image} alt={s.label} className="w-full h-28 object-cover" />
                  <p className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-2 ${freeHoodieStyle === s.key ? 'text-white' : 'text-gray-400'}`}>
                    {s.label}
                  </p>
                </button>
                );
              })}
            </div>
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">
              Select Size {freeHoodieSize && <span className="text-[#D4AF37]">— {freeHoodieSize}</span>}
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {HOODIE_SIZES.map(size => (
                <button
                  key={size}
                  onClick={() => setFreeHoodieSize(size)}
                  className={`min-w-[48px] border rounded-sm px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${
                    freeHoodieSize === size ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white' : 'border-[#27272A] text-gray-400 hover:border-gray-500'
                  }`}
                  data-testid={`free-hoodie-size-${size}`}
                >
                  {size}
                </button>
              ))}
            </div>
            <button
              onClick={handleAddFreeHoodie}
              disabled={!freeHoodieStyle || !freeHoodieSize}
              className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold uppercase tracking-wider py-3 rounded-sm transition-colors"
              data-testid="add-free-hoodie-btn"
            >
              {freeHoodieInCart ? 'Update My Free Hoodie' : 'Add My Free Hoodie'}
            </button>
            {freeHoodieInCart && (
              <p className="text-green-400 text-xs font-semibold mt-2 flex items-center gap-1.5" data-testid="free-hoodie-added-note">
                <CheckCircle size={13} />
                {freeHoodieInCart.name} ({freeHoodieInCart.variantLabel}) is in your cart — FREE!
              </p>
            )}
          </div>
        )}

        {/* Bac Water 28-day usage notice modal */}
        {showBacNotice && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="bac-notice-modal">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <div className="relative bg-[#0A0A0A] border border-[#D4AF37] rounded-sm max-w-md w-full p-6">
              <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-3">⚠ Important — Bacteriostatic Water</p>
              <p className="text-gray-200 text-sm leading-relaxed mb-5">
                Once opened, bacteriostatic water should be used within <span className="text-white font-semibold">28 days</span>. After 28 days, discard any remaining solution and use a new vial. Store as directed and always use sterile technique when handling.
              </p>
              <button
                onClick={() => { setShowBacNotice(false); submitOrder(); }}
                className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-colors"
                data-testid="bac-notice-confirm"
              >
                I Understand — Place My Order
              </button>
            </div>
          </div>
        )}

        {/* Bac Water Upsell */}
        {showBacWaterUpsell && (
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/60 rounded-sm p-4 mb-4 flex items-center gap-4" data-testid="bac-water-upsell">
            <div className="w-14 h-14 bg-[#121212] rounded-sm flex-shrink-0 overflow-hidden">
              {bacWaterProduct.image && (
                <img src={bacWaterProduct.image} alt="Bacteriostatic Water" className="w-full h-full object-contain" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#D4AF37] text-sm font-semibold">Don&apos;t forget bac water!</p>
              <p className="text-gray-400 text-xs mt-0.5">
                You&apos;ll need bacteriostatic water to reconstitute your peptides — add a {bacWaterProduct.dosage} vial for {bacWaterProduct.price}.
              </p>
            </div>
            <button
              onClick={() => addToCart(bacWaterProduct)}
              className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors whitespace-nowrap flex-shrink-0"
              data-testid="bac-water-upsell-add"
            >
              Add — {bacWaterProduct.price}
            </button>
          </div>
        )}

        {/* Shipping Method */}
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 mb-4" data-testid="shipping-method-section">
          <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-4">Delivery Method</h2>
          <div className="space-y-2">
            {SHIPPING_OPTIONS.map(opt => (
              <button
                key={opt.id}
                onClick={() => setShipMethod(opt.id)}
                className={`w-full flex items-start gap-3 text-left border rounded-sm px-4 py-3 transition-colors ${shipMethod === opt.id ? 'border-[#C41E3A] bg-[#C41E3A]/10' : 'border-[#27272A] bg-[#121212] hover:border-gray-500'}`}
                data-testid={`ship-option-${opt.id}`}
              >
                <span className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 ${shipMethod === opt.id ? 'border-[#C41E3A] bg-[#C41E3A]' : 'border-gray-500'}`} />
                <span className="flex-1">
                  <span className="flex justify-between items-center">
                    <span className="text-white text-sm font-semibold">{opt.label}</span>
                    <span className={`font-bold text-sm ${opt.price === 0 ? 'text-green-400' : 'text-[#D4AF37]'}`}>{opt.price === 0 ? 'FREE' : `$${opt.price}`}</span>
                  </span>
                  <span className="text-gray-500 text-xs block mt-0.5">{opt.desc}</span>
                </span>
              </button>
            ))}
          </div>
          <ul className="text-gray-400 text-xs space-y-1 mt-4">
            <li>We currently ship within <span className="text-[#D4AF37] font-semibold">Canada only</span>.</li>
            <li><span className="text-[#D4AF37] font-semibold">COAs available upon request</span>.</li>
          </ul>
        </div>

        {/* Mailing Address */}
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 mb-4">
          <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-4">{isPickup ? 'Pickup Contact Info' : 'Mailing Address'}</h2>
          {savedAddresses.length > 0 && (
            <div className="mb-4" data-testid="saved-address-picker">
              <p className="text-gray-500 text-xs uppercase tracking-wider font-bold mb-2">Use a saved address</p>
              <div className="flex flex-wrap gap-2">
                {savedAddresses.map(a => (
                  <button
                    key={a.addr_id}
                    type="button"
                    onClick={() => setAddress({ name: a.name, street: a.street, city: a.city, province: a.province, postal: a.postal, phone: a.phone })}
                    className="text-left bg-[#121212] border border-[#27272A] hover:border-[#C41E3A] rounded-sm px-3 py-2 transition-colors"
                    data-testid={`use-saved-address-${a.addr_id}`}
                  >
                    <span className="block text-white text-xs font-semibold">{a.label || a.name}</span>
                    <span className="block text-gray-500 text-[11px]">{a.street}, {a.city}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Full Name *"
              value={address.name}
              onChange={e => setAddress(prev => ({ ...prev, name: e.target.value }))}
              className="w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
              data-testid="address-name"
            />
            {!isPickup && (<>
            <input
              type="text"
              placeholder="Street Address *"
              value={address.street}
              onChange={e => setAddress(prev => ({ ...prev, street: e.target.value }))}
              className="w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
              data-testid="address-street"
            />
            <div className="grid grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="City *"
                value={address.city}
                onChange={e => setAddress(prev => ({ ...prev, city: e.target.value }))}
                className="bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
                data-testid="address-city"
              />
              <input
                type="text"
                placeholder="Province"
                value={address.province}
                onChange={e => setAddress(prev => ({ ...prev, province: e.target.value }))}
                className="bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
                data-testid="address-province"
              />
              <input
                type="text"
                placeholder="Postal Code *"
                value={address.postal}
                onChange={e => setAddress(prev => ({ ...prev, postal: e.target.value }))}
                className="bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
                data-testid="address-postal"
              />
            </div>
            </>)}
            <input
              type="tel"
              placeholder="Phone Number *"
              value={address.phone}
              onChange={e => setAddress(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
              data-testid="address-phone"
            />
            <p className="text-gray-500 text-xs">{isPickup ? "We'll use your phone number to arrange your pickup time in Airdrie." : "We only use your phone number to contact you if there's an issue with your order or address."}</p>
            {addressError && (
              <p className="text-[#C41E3A] text-sm" data-testid="address-error">{addressError}</p>
            )}
          </div>
        </div>

        {/* Customer Email */}
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 mb-4">
          <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-2">Your Email</h2>
          <p className="text-gray-400 text-xs mb-3">Please add your email so we can send you mixing and dosing protocols</p>
          <input
            type="email"
            placeholder="your@email.com"
            value={customerEmail}
            onChange={e => setCustomerEmail(e.target.value)}
            className="w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
            data-testid="customer-email"
          />
          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={subscribeNews}
              onChange={e => setSubscribeNews(e.target.checked)}
              className="w-4 h-4 accent-[#C41E3A] cursor-pointer"
              data-testid="subscribe-checkbox"
            />
            <span className="text-gray-400 text-xs">Be the first to know about new products, deals &amp; promos</span>
          </label>
        </div>

        {/* Promo Code */}
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 mb-4">
          <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-2">Promo Code</h2>
          <p className="text-gray-400 text-xs mb-3">Enter your email above before applying a promo code</p>
          {promoApplied ? (
            <div className="flex items-center justify-between bg-[#121212] border border-green-600/40 rounded-sm px-4 py-3">
              <div className="flex items-center gap-2">
                <CheckCheck size={18} className="text-green-400" />
                <span className="text-green-400 font-semibold text-sm">{activePromo?.code}</span>
                <span className="text-gray-500 text-xs">
                  {activePromo?.halloween ? (discountAmount > 0 ? `— Halloween sale prices applied! You're saving $${discountAmount.toFixed(2)}` : '— Halloween sale active! Add sale items to see your sale prices') : activePromo?.freeCase ? `— Free ${promoConfig?.free_item?.dosage?.split('•')[0].trim().toLowerCase() || ''} peptide case added!` : activePromo?.freeHoodie ? (freeHoodieInCart ? '— Free hoodie added!' : '— Free hoodie unlocked! Choose your style above') : activePromo?.fixed ? `— $${discountAmount.toFixed(0)} off applied ($25/KLOW, $20/GLOW)` : activePromo?.stackedBonus ? `— Thank-you bonus! ${activePromo?.baseDiscount}% + 10% stacked = ${activePromo?.discount}% off peptides` : `— ${activePromo?.discount}% off peptides applied`}
                </span>
              </div>
              <button
                onClick={handleRemovePromo}
                className="text-gray-500 hover:text-[#C41E3A] text-xs uppercase tracking-wider transition-colors"
                data-testid="remove-promo-btn"
              >
                Remove
              </button>
            </div>
          ) : (
            <div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={e => { setPromoCode(e.target.value); setPromoError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
                  className="flex-1 bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors uppercase tracking-wider"
                  data-testid="promo-code-input"
                />
                <button
                  onClick={handleApplyPromo}
                  className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-sm font-bold uppercase tracking-wider px-5 py-3 rounded-sm transition-all duration-300"
                  data-testid="apply-promo-btn"
                >
                  Apply
                </button>
              </div>
              {promoError && (
                <p className="text-[#C41E3A] text-sm mt-2" data-testid="promo-error">{promoError}</p>
              )}
            </div>
          )}
        </div>

        {/* Total & Checkout */}
        <div className="bg-[#0A0A0A] border border-[#C41E3A] rounded-sm p-6">
          <div className="space-y-2 mb-4">
            <div className="flex justify-between items-center">
              <p className="text-gray-400 text-sm">Subtotal</p>
              <p className="text-white font-semibold">${cartTotal.toFixed(2)}</p>
            </div>
            {promoApplied && activePromo?.freeCase && (
              <div className="flex justify-between items-center">
                <p className="text-green-400 text-sm flex items-center gap-1.5">
                  <Gift size={13} />
                  {activePromo?.code} — Free Peptide Case
                </p>
                <p className="text-green-400 font-semibold">FREE</p>
              </div>
            )}
            {promoApplied && activePromo?.freeHoodie && freeHoodieInCart && (
              <div className="flex justify-between items-center">
                <p className="text-green-400 text-sm flex items-center gap-1.5">
                  <Gift size={13} />
                  {activePromo?.code} — Free Hoodie ({freeHoodieInCart.variantLabel})
                </p>
                <p className="text-green-400 font-semibold">FREE</p>
              </div>
            )}
            {promoApplied && discountAmount > 0 && (
              <div className="flex justify-between items-center">
                <p className="text-green-400 text-sm flex items-center gap-1.5">
                  <Tag size={13} />
                  {activePromo?.code} {activePromo?.fixed ? '($ off KLOW/GLOW)' : `(${activePromo?.discount}% off)`}
                </p>
                <p className="text-green-400 font-semibold">-${discountAmount.toFixed(2)}</p>
              </div>
            )}
            <div className="flex justify-between items-center">
              <p className="text-gray-400 text-sm">{isPickup ? 'Pickup — Airdrie' : 'Shipping'}</p>
              <p className={`font-semibold ${SHIPPING_COST === 0 ? 'text-green-400' : 'text-white'}`}>{SHIPPING_COST === 0 ? 'FREE' : `$${SHIPPING_COST.toFixed(2)}`}</p>
            </div>
            <div className="border-t border-[#27272A] pt-2 flex justify-between items-center">
              <p className="text-white font-bold text-xl uppercase">Total</p>
              <p className="text-[#C41E3A] text-3xl font-bold" data-testid="cart-total">${orderTotal.toFixed(2)}</p>
            </div>
            <p className="text-[#D4AF37] text-xs">{isPickup ? 'Free pickup — no shipping charge' : 'Shipping added to total price'}</p>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Payment via <span className="text-[#D4AF37] font-semibold">Interac e-Transfer</span> to canadianwellness2026@outlook.com
          </p>
          <button
            onClick={handleCheckout}
            disabled={submitting || !address.name.trim() || (!isPickup && (!address.street.trim() || !address.city.trim() || !address.province.trim() || !address.postal.trim())) || !address.phone.trim() || !customerEmail.trim()}
            className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-all duration-300 btn-glow flex items-center justify-center gap-2"
            data-testid="checkout-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Submitting Order...
              </>
            ) : (
              'Place Order'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
